import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { Chip } from '@mui/material';
import PsychologyAltIcon from '@mui/icons-material/PsychologyAlt';
import { appointmentsAPI, wellnessAPI, aiAPI } from 'resources/api';
import {
    DARKER_TEXT,
    DARK_TEXT,
    BRAND_GREEN_PRIMARY,
    SUCCESS_GREEN,
    WARNING_ORANGE,
    ERROR_RED,
} from 'resources/constants/colors';
import { IMAGES_URL } from 'resources/constants/urls';
import NoProfileSVG from 'resources/img/no-pic-therapist.png';
import useUser from 'state/user';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const Page = styled.div`
    padding: 16px;
    padding-bottom: 80px;
    min-height: 100%;
    background: #f5f5f5;
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 20px;
`;

const BackBtn = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px;
    color: ${BRAND_GREEN_PRIMARY};
    display: flex;
    align-items: center;
`;

const HeaderTitle = styled.h2`
    font-size: 20px;
    color: ${BRAND_GREEN_PRIMARY};
    margin: 0;
    font-weight: 700;
`;

const ProfileCard = styled.div`
    background: #fff;
    border-radius: 16px;
    padding: 20px;
    text-align: center;
    box-shadow: 0 2px 10px rgba(0,0,0,0.06);
    margin-bottom: 16px;
`;

const Avatar = styled.img`
    width: 72px;
    height: 72px;
    border-radius: 50%;
    object-fit: cover;
    border: 3px solid ${BRAND_GREEN_PRIMARY};
`;

const PatientName = styled.h3`
    font-size: 18px;
    color: ${DARKER_TEXT};
    margin: 10px 0 4px;
`;

const PatientEmail = styled.p`
    font-size: 13px;
    color: ${DARK_TEXT};
    margin: 0;
`;

const Section = styled.div`
    background: #fff;
    border-radius: 16px;
    padding: 16px;
    box-shadow: 0 2px 10px rgba(0,0,0,0.06);
    margin-bottom: 16px;
`;

const SectionTitle = styled.h4`
    font-size: 14px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #aaa;
    margin: 0 0 12px;
`;

const StatRow = styled.div`
    display: flex;
    gap: 8px;
`;

const StatCard = styled.div`
    flex: 1;
    background: ${({ bg }) => bg || '#f8f8f8'};
    border-radius: 12px;
    padding: 10px;
    text-align: center;
`;

const StatValue = styled.p`
    font-size: 22px;
    font-weight: 800;
    color: ${DARKER_TEXT};
    margin: 0;
`;

const StatLabel = styled.p`
    font-size: 10px;
    color: ${DARK_TEXT};
    margin: 2px 0 0;
`;

const NoteCard = styled.div`
    background: #fafafa;
    border-radius: 12px;
    padding: 14px;
    margin-bottom: 10px;
    border-left: 4px solid ${({ riskColor }) => riskColor || BRAND_GREEN_PRIMARY};

    &:last-child { margin-bottom: 0; }
`;

const NoteDate = styled.p`
    font-size: 11px;
    color: ${DARK_TEXT};
    margin: 0 0 6px;
`;

const NoteField = styled.div`
    margin-bottom: 6px;
    &:last-child { margin-bottom: 0; }
`;

const NoteFieldLabel = styled.span`
    font-size: 11px;
    font-weight: 700;
    color: #aaa;
    text-transform: uppercase;
`;

const NoteFieldValue = styled.p`
    font-size: 13px;
    color: ${DARKER_TEXT};
    margin: 2px 0 0;
    white-space: pre-wrap;
`;

const RiskBadge = styled.span`
    display: inline-block;
    font-size: 11px;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 10px;
    color: #fff;
    background: ${({ color }) => color};
    margin-left: 8px;
`;

const EmptyMsg = styled.p`
    font-size: 13px;
    color: ${DARK_TEXT};
    text-align: center;
    padding: 20px 0;
    margin: 0;
`;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const RISK_COLORS = {
    NONE: '#ccc',
    LOW: SUCCESS_GREEN,
    MEDIUM: WARNING_ORANGE,
    HIGH: ERROR_RED,
    CRITICAL: '#b71c1c',
};

const RISK_LABELS = {
    NONE: 'Ninguno',
    LOW: 'Bajo',
    MEDIUM: 'Medio',
    HIGH: 'Alto',
    CRITICAL: 'Crítico',
};

const MOOD_EMOJIS = { 1: '😢', 2: '😔', 3: '😐', 4: '🙂', 5: '😊' };

const formatDate = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    return dt.toLocaleDateString('es-MX', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const Expediente = () => {
    const { clientId } = useParams();
    const history = useHistory();
    const [user] = useUser();
    const [notes, setNotes] = useState([]);
    const [wellness, setWellness] = useState(null);
    const [loading, setLoading] = useState(true);
    const [aiEnabled, setAiEnabled] = useState(false);

    // Find patient info from therapist's client list
    const clients = user.current?.extraData?.clients || [];
    const patient = clients.find(c => String(c.id) === String(clientId));

    useEffect(() => {
        if (!clientId) return;
        let cancelled = false;

        const load = async () => {
            setLoading(true);
            try {
                const [historyRes, wellnessRes] = await Promise.all([
                    appointmentsAPI.getClientHistory(clientId),
                    wellnessAPI.getPatientSummary(clientId, 30),
                ]);
                if (cancelled) return;
                setNotes(historyRes?.data || historyRes || []);
                setWellness(wellnessRes?.data || wellnessRes || null);
            } catch (e) {
                /* silencioso */
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [clientId]);

    // Fetch AI feature status (solo para terapeutas)
    useEffect(() => {
        if (user.current?.userType !== 'therapist') return;
        aiAPI.getFeatureStatus()
            .then((data) => {
                if (data?.aiEnabled) setAiEnabled(true);
            })
            .catch(() => {});
    }, [user.current?.userType]);

    const sosCount = wellness?.sosEvents?.length || 0;
    const activityCount = wellness?.activities?.length || 0;
    const journalCount = wellness?.journals?.length || 0;
    const moodAvg = wellness?.trends?.averageMood;

    return (
        <Page>
            {/* Header */}
            <Header>
                <BackBtn onClick={() => history.goBack()}>
                    <ArrowBackIosNewIcon fontSize="small" />
                </BackBtn>
                <HeaderTitle>Expediente Clínico</HeaderTitle>
                {aiEnabled && (
                    <Chip
                        label="IA Activa"
                        color="success"
                        size="small"
                        icon={<PsychologyAltIcon />}
                        sx={{ ml: 'auto', fontWeight: 700, fontSize: 11 }}
                    />
                )}
            </Header>

            {/* Patient Profile */}
            <ProfileCard>
                <Avatar
                    src={patient?.profileImg ? `${IMAGES_URL}${patient.profileImg}` : NoProfileSVG}
                    alt={patient?.name || 'Paciente'}
                />
                <PatientName>
                    {patient ? `${patient.name} ${patient.lastName}` : `Paciente #${clientId}`}
                </PatientName>
                {patient?.email && <PatientEmail>{patient.email}</PatientEmail>}
            </ProfileCard>

            {/* Telemetry Summary */}
            <Section>
                <SectionTitle>Actividad (30 días)</SectionTitle>
                <StatRow>
                    <StatCard bg={moodAvg ? '#e8f5e9' : undefined}>
                        <StatValue>
                            {moodAvg ? `${MOOD_EMOJIS[Math.round(moodAvg)] || ''} ${moodAvg.toFixed(1)}` : '—'}
                        </StatValue>
                        <StatLabel>Mood promedio</StatLabel>
                    </StatCard>
                    <StatCard bg={sosCount > 0 ? '#fce4ec' : undefined}>
                        <StatValue>{sosCount}</StatValue>
                        <StatLabel>Eventos SOS</StatLabel>
                    </StatCard>
                    <StatCard>
                        <StatValue>{activityCount}</StatValue>
                        <StatLabel>Ejercicios</StatLabel>
                    </StatCard>
                    <StatCard>
                        <StatValue>{journalCount}</StatValue>
                        <StatLabel>Diario</StatLabel>
                    </StatCard>
                </StatRow>
            </Section>

            {/* Clinical Notes History */}
            <Section>
                <SectionTitle>
                    <DescriptionOutlinedIcon style={{ fontSize: 16, verticalAlign: 'text-bottom', marginRight: 4 }} />
                    Notas Clínicas (SOAP)
                </SectionTitle>

                {loading && <EmptyMsg>Cargando historial...</EmptyMsg>}

                {!loading && notes.length === 0 && (
                    <EmptyMsg>No hay notas clínicas registradas para este paciente.</EmptyMsg>
                )}

                {!loading && notes.map((note) => {
                    const risk = note.riskLevel || 'NONE';
                    return (
                        <NoteCard key={note.id} riskColor={RISK_COLORS[risk]}>
                            <NoteDate>
                                {formatDate(note.signedAt || note.createdAt)}
                                <RiskBadge color={RISK_COLORS[risk]}>{RISK_LABELS[risk]}</RiskBadge>
                            </NoteDate>
                            {note.subjective && (
                                <NoteField>
                                    <NoteFieldLabel>S — Subjetivo</NoteFieldLabel>
                                    <NoteFieldValue>{note.subjective}</NoteFieldValue>
                                </NoteField>
                            )}
                            {note.objective && (
                                <NoteField>
                                    <NoteFieldLabel>O — Objetivo</NoteFieldLabel>
                                    <NoteFieldValue>{note.objective}</NoteFieldValue>
                                </NoteField>
                            )}
                            {note.assessment && (
                                <NoteField>
                                    <NoteFieldLabel>A — Evaluación</NoteFieldLabel>
                                    <NoteFieldValue>{note.assessment}</NoteFieldValue>
                                </NoteField>
                            )}
                            {note.plan && (
                                <NoteField>
                                    <NoteFieldLabel>P — Plan</NoteFieldLabel>
                                    <NoteFieldValue>{note.plan}</NoteFieldValue>
                                </NoteField>
                            )}
                        </NoteCard>
                    );
                })}
            </Section>
        </Page>
    );
};

export default Expediente;
